import type { SalesforceOpportunityIngestRequest } from '@/generated/http-clients/v3ApiSchemas'
import { DataList } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'

type Props = {
  payload: SalesforceOpportunityIngestRequest
  licenceTotal: number
}

/**
 * Read-only echo of a parsed payload block.
 *
 * The point of the whole paste-and-parse design: the operator is about to
 * trigger up to 100 licence creations against a named organisation, so they
 * see which case they pasted before they choose how to resolve it.
 *
 * Shows the licence span only when every line agrees on it. Lines may carry
 * different dates, and collapsing several spans into one would misreport the
 * payload in exactly the situation the panel exists to guard.
 */
export function ParsedPayloadPanel({ payload, licenceTotal }: Readonly<Props>) {
  const { t } = useTranslation('features', { useSuspense: false })

  const [first] = payload.licenseLines
  const sharedSpan = payload.licenseLines.every(
    (line) =>
      line.validFrom === first.validFrom && line.validTo === first.validTo,
  )

  return (
    <DataList.Root orientation="horizontal" divideY="1px" size="sm">
      <DataList.Item pt={2}>
        <DataList.ItemLabel>
          {String(t('salesforceIngest.parsed.opportunityId'))}
        </DataList.ItemLabel>
        <DataList.ItemValue fontFamily="mono">
          {payload.opportunityId}
        </DataList.ItemValue>
      </DataList.Item>

      <DataList.Item pt={2}>
        <DataList.ItemLabel>
          {String(t('salesforceIngest.parsed.salesforceAccountId'))}
        </DataList.ItemLabel>
        <DataList.ItemValue fontFamily="mono">
          {payload.salesforceAccountId}
        </DataList.ItemValue>
      </DataList.Item>

      <DataList.Item pt={2}>
        <DataList.ItemLabel>
          {String(t('salesforceIngest.parsed.owner'))}
        </DataList.ItemLabel>
        <DataList.ItemValue>
          {String(
            t('salesforceIngest.parsed.ownerValue', {
              email: payload.orgOwner.email,
              firstName: payload.orgOwner.firstName,
              lastName: payload.orgOwner.lastName,
            }),
          )}
        </DataList.ItemValue>
      </DataList.Item>

      <DataList.Item pt={2}>
        <DataList.ItemLabel>
          {String(t('salesforceIngest.parsed.company'))}
        </DataList.ItemLabel>
        <DataList.ItemValue>{payload.company}</DataList.ItemValue>
      </DataList.Item>

      <DataList.Item pt={2}>
        <DataList.ItemLabel>
          {String(t('salesforceIngest.parsed.licences'))}
        </DataList.ItemLabel>
        <DataList.ItemValue>
          {String(
            t(
              sharedSpan
                ? 'salesforceIngest.parsed.licencesValue'
                : 'salesforceIngest.parsed.licencesValueMixed',
              {
                count: licenceTotal,
                lines: payload.licenseLines.length,
                validFrom: first.validFrom,
                validTo: first.validTo,
              },
            ),
          )}
        </DataList.ItemValue>
      </DataList.Item>
    </DataList.Root>
  )
}
